import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, FileIcon, AlertCircle, CheckCircle } from 'lucide-react';
import { formatFileSize } from '../mockData';

const SharedFilePage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('t');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                // We use the download endpoint with the token to "validate" access
                // Since we don't have a dedicated public metadata endpoint for tokens yet,
                // we'll try to fetch metadata if public, or just rely on the download capability.
                // Actually, for a better UX, let's try to get metadata first.
                // Note: The backend /files/:id/metadata might require auth.
                // Let's rely on the file list or download for now.

                // Strategy: Try to download 1 byte or HEAD? 
                // Or just show a generic "Ready to Download" and fetch on click.
                // But we want to show filename/size.

                // Let's try to fetch metadata. If it fails (403), we assume we need the token for download.
                // But wait, if the token is for download, we can't easily get metadata without downloading the file
                // unless we update the backend.
                // WORKAROUND: For now, we'll auto-trigger a "pre-flight" check or just show a "Download Shared File" button.
                // However, to make it nice, let's assume valid token means we can download.

                // Let's blindly trust the ID for a moment or try to fetch public info.
                // Ideally, we update backend to allow metadata fetch with token.
                // For this iteration, let's try to fetch metadata. If it fails, we interpret as "needs token to download".

                // Update: Backend /files/:id check:
                // GET /files/:id with ?t=token allows download.
                // It returns the file content (Blob).
                // It sets Content-Disposition header with filename.

                // So we can't easily show "File Name: foo.pdf" before downloading without downloading it.
                // Unless we change backend.
                // Let's just show a generic "Shared File" card and let them click download.
                // Once download starts, we can maybe show info? No, it's a stream.

                setLoading(false);
                setFile({ id, name: 'Shared File' }); // Placeholder

            } catch (err) {
                console.error(err);
                setError('Invalid link or expired token.');
                setLoading(false);
            }
        };

        if (id && token) {
            fetchMetadata();
        } else {
            setError('Missing file ID or token.');
            setLoading(false);
        }
    }, [id, token]);

    const handleDownload = async () => {
        setDownloading(true);
        setError(null);
        try {
            const { filesApi } = await import('../lib/api');
            const blob = await filesApi.download(id, token);

            // Extract filename from response headers if possible, or fallback
            // Since axios returns data, we might need access to headers.
            // frontend/src/lib/api.js `download` returns `response.data`.
            // We might need to adjust api.js if we want headers.
            // For now, let's just use the blob.

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // Best guess default name, browsre might override with header
            a.download = 'downloaded_file';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setDownloading(false);
        } catch (err) {
            console.error('Download failed', err);
            setError('Failed to download. The link may have expired.');
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <Card className="max-w-md w-full p-8 text-center text-red-600">
                    <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-80" />
                    <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                    <p className="text-gray-600">{error}</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <Card className="max-w-md w-full p-8 text-center">
                <div className="bg-teal-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <FileIcon className="h-10 w-10 text-teal-600" />
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">Shared File</h1>
                <p className="text-gray-500 mb-8">
                    You have been granted access to download this file.
                </p>

                <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white h-12 text-lg"
                >
                    {downloading ? (
                        <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Downloading...
                        </div>
                    ) : (
                        <div className="flex items-center">
                            <Download className="h-5 w-5 mr-2" />
                            Download File
                        </div>
                    )}
                </Button>

                <p className="text-xs text-gray-400 mt-6">
                    Secure file sharing via Jeevi File Hub
                </p>
            </Card>
        </div>
    );
};

export default SharedFilePage;
